import { RequestStatus } from '@core/enums/request-status.enum';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { exportAuditThunk, getAuditThunk } from '@redux/features/audit.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import Pagination from 'Common/Pagination';
import React, { FC, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import DebouncedInput from 'Shared/DebouncedInput';
import ExportButton from 'Shared/ExportButton';
import FilterInput from 'Shared/FilterInput';

const Audit: FC = () => {
    // -----------------------------------------------------------------
    const [page, setPage] = useState<number>(1);
    const [username, setUsername] = useState<string>('');
    const [actionType, setActionType] = useState<string>('');
    const [entityType, setEntityType] = useState<string>('');
    const [status, setStatus] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    // -----------------------------------------------------------------

    const dispatch = useAppDispatch();
    const { audit, loading } = useAppSelector((state: RootState) => state.auditState);
    // -----------------------------------------------------------------

    const changePage = (p: string) => setPage(Number(p));
    // -----------------------------------------------------------------

    // Helper function to format date to YYYY-MM-DDTHH:MM:SS format
    const formatDateForAPI = (date: Date, isEndDate: boolean = false): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const time = isEndDate ? '23:59:59' : '00:00:00';
        return `${year}-${month}-${day}T${time}`;
    };

    // Handle date range changes - only send when both dates are selected
    const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
        setDateRange(dates);
        const [start, end] = dates;
        if (start && end) {
            setStartDate(formatDateForAPI(start, false));
            setEndDate(formatDateForAPI(end, true));
        } else if (!start && !end) {
            setStartDate('');
            setEndDate('');
        }
    };

    // -----------------------------------------------------------------

    useEffect(() => {
        dispatch(
            getAuditThunk({
                size: 10,
                page: page - 1,
                username: username || '',
                actionType: actionType || '',
                entityType: entityType || '',
                status: status || '',
                ipAddress: '',
                entityId: '',
                startDate: startDate || '',
                endDate: endDate || '',
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, username, actionType, entityType, status, startDate, endDate]);
    // -----------------------------------------------------------------

    const formatDateDisplay = (dateString: string | null | undefined) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    // -----------------------------------------------------------------

    const handleExport = () => {
        dispatch(
            exportAuditThunk({
                startDate: startDate || '',
                endDate: endDate || '',
                username: username || '',
                actionType: actionType || '',
            })
        ).then((res: any) => {
            if (res.meta.requestStatus === RequestStatus.FULFILLED) {
                const downloadUrl = res.payload?.exportRequest?.downloadUrl || res.payload?.exportRequest?.filePath;
                console.log(res);
                if (downloadUrl) {
                    const a = document.createElement('a');
                    a.href = downloadUrl;
                    a.download = 'Audit.csv';
                    a.target = '_blank';
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                } else {
                    toast.error('Download URL not found in response', { autoClose: 2000 });
                }
            }
        });
    };

    // -----------------------------------------------------------------

    return (
        <>
            <BreadCrumb pageTitle="Audit" />
            <section className="py-5">
                <div className=" p-3 flex flex-col gap-2 bg-[#fff]">
                    <div className="flex w-full items-center justify-between">
                        <h3 className="font-extrabold text-[#bd902e]">Audit Logs</h3>
                    </div>
                    <div className="flex flex-col gap-4">
                        <div className="flex gap-5 flex-wrap">
                            <div className="flex-1 min-w-[220px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Username</label>
                                <DebouncedInput
                                    placeholder="Search username"
                                    searchValue={username}
                                    searchFn={setUsername}
                                    // searchFn={() => setPage(1)}
                                />
                            </div>
                            <div className="flex-1 min-w-[220px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Action Type</label>
                                <DebouncedInput
                                    placeholder="Search action type"
                                    searchValue={actionType}
                                    searchFn={setActionType}
                                    // searchFn={() => setPage(1)}
                                />
                            </div>
                            
                            <div className="flex-1 min-w-[220px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Entity Type</label>
                                <DebouncedInput
                                    placeholder="Search entity type"
                                    searchValue={entityType}
                                    searchFn={setEntityType}
                                    // searchFn={() => setPage(1)}
                                />
                            </div>
                            <div className="flex-1 min-w-[220px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Status</label>
                                <FilterInput
                                    value={status}
                                    onChange={(v) => setStatus(v)}
                                    // filterFn={() => setPage(1)}
                                    options={[
                                        { value: '', label: 'All Status' },
                                        { value: 'SUCCESS', label: 'SUCCESS' },
                                        { value: 'FAILED', label: 'FAILED' },
                                    ]}
                                    id="status"
                                    placeholder="All Status"
                                    optionLabelKey="label"
                                    optionValueKey="value"
                                />
                            </div>
                            <div className="flex-1 min-w-[320px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Date Range</label>
                                <DatePicker
                                    selected={dateRange[0]}
                                    onChange={handleDateRangeChange}
                                    startDate={dateRange[0]}
                                    endDate={dateRange[1]}
                                    selectsRange
                                    isClearable
                                    placeholderText="Select date range"
                                    className="form-input border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400 w-full transition-all duration-200"
                                    dateFormat="yyyy-MM-dd"
                                />
                            </div>
                            <div className="ml-auto flex items-end">
                                <ExportButton onExport={handleExport} />
                            </div>
                        </div>
                    </div>
                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Action Date</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Username</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Action Type</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Entity Type</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Entity ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Status</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">IP Address</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Device Info</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Correlation ID
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Reason</th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={loading} itemCount={(audit as any)?.content?.length ?? 0} animationKey={`audit-${page}`}>
                                            {(audit as any)?.content?.map((item: any) => (
                                                <AnimatedTableRow
                                                    rowKey={item?.id}
                                                    detailRecord={item}
                                                    className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                >
                                                    <td className="px-3.5 py-2.5">{item?.id || '-'}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        {formatDateDisplay(item?.actionDate)}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">{item?.username || '-'}</td>
                                                    <td className="px-3.5 py-2.5">{item?.actionType || '-'}</td>
                                                    <td className="px-3.5 py-2.5">{item?.entityType || '-'}</td>
                                                    <td className="px-3.5 py-2.5">{item?.entityId || '-'}</td>
                                                    <td className="px-3.5 py-2.5">{item?.status || '-'}</td>
                                                    <td className="px-3.5 py-2.5">{item?.ipAddress || '-'}</td>
                                                    <td className="px-3.5 py-2.5">{item?.deviceInfo || '-'}</td>
                                                    <td className="px-3.5 py-2.5">{item?.correlationId || '-'}</td>
                                                    <td
                                                        className="px-3.5 py-2.5 max-w-[320px] truncate"
                                                        title={item?.reason || ''}
                                                    >
                                                        {item?.reason || '-'}
                                                    </td>
                                                </AnimatedTableRow>
                                            ))}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                                <Pagination
                                    perPageData={(audit as any)?.pageable?.pageSize || 10}
                                    total={(audit as any)?.totalElements || 0}
                                    currentPage={page}
                                    changePage={changePage}
                                    length={(audit as any)?.numberOfElements || (audit as any)?.content?.length || 0}
                                />
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default Audit;
